import { Children, isValidElement, useEffect, useRef } from "react";

function createChangeEvent(event) {
  const value = event.detail ?? event.target?.value ?? "";
  return {
    currentTarget: { value },
    nativeEvent: event,
    target: { value }
  };
}

function booleanAttribute(value) {
  return value ? "" : undefined;
}

function useValueChange(ref, value, onChange) {
  useEffect(() => {
    if (ref.current && ref.current.value !== value) {
      ref.current.value = value ?? "";
    }
  }, [ref, value]);

  useEffect(() => {
    const element = ref.current;
    if (!element || !onChange) {
      return undefined;
    }

    const handleValueChange = (event) => onChange(createChangeEvent(event));
    element.addEventListener("valueChange", handleValueChange);
    return () => element.removeEventListener("valueChange", handleValueChange);
  }, [ref, onChange]);
}

export function GovBrButton({
  children,
  disabled,
  emphasis = "secondary",
  loading,
  type = "button",
  ...props
}) {
  return (
    <br-button
      disabled={booleanAttribute(disabled)}
      emphasis={emphasis}
      loading={booleanAttribute(loading)}
      type={type}
      {...props}
    >
      {children}
    </br-button>
  );
}

export function GovBrInput({
  disabled,
  onChange,
  value = "",
  ...props
}) {
  const ref = useRef(null);
  useValueChange(ref, value, onChange);

  return (
    <br-input
      ref={ref}
      disabled={booleanAttribute(disabled)}
      value={String(value ?? "")}
      {...props}
    ></br-input>
  );
}

export function GovBrTextarea({
  disabled,
  onChange,
  value = "",
  ...props
}) {
  const ref = useRef(null);
  useValueChange(ref, value, onChange);

  return (
    <br-textarea
      ref={ref}
      disabled={booleanAttribute(disabled)}
      value={String(value ?? "")}
      {...props}
    ></br-textarea>
  );
}

export function GovBrSelect({
  children,
  disabled,
  onChange,
  value = "",
  ...props
}) {
  const ref = useRef(null);
  const selectedValue = String(value ?? "");
  const options = Children.toArray(children)
    .filter((child) => isValidElement(child) && child.type === "option")
    .map((child) => {
      const optionValue = String(child.props.value ?? "");
      return {
        label: String(child.props.children ?? child.props.value ?? ""),
        value: optionValue,
        selected: optionValue === selectedValue,
        disabled: Boolean(child.props.disabled)
      };
    });
  const serializedOptions = JSON.stringify(options);

  useValueChange(ref, selectedValue, onChange);

  useEffect(() => {
    if (ref.current) {
      ref.current.options = JSON.parse(serializedOptions);
    }
  }, [serializedOptions]);

  return (
    <br-select
      ref={ref}
      disabled={booleanAttribute(disabled)}
      options={serializedOptions}
      value={selectedValue}
      {...props}
    ></br-select>
  );
}
